import React, { useEffect, useRef, useState } from 'react';

const GoogleTranslate = ({ className = '' }) => {
  const googleTranslateRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (isLoaded || error) return;

    // Function to initialize Google Translate
    const initializeGoogleTranslate = () => {
      try {
        if (
          window.google &&
          window.google.translate &&
          window.google.translate.TranslateElement &&
          googleTranslateRef.current
        ) {
          // Clear any existing content first
          googleTranslateRef.current.innerHTML = '';

          new window.google.translate.TranslateElement(
            {
              pageLanguage: 'en',
              includedLanguages:
                'en,es,fr,de,it,pt,ru,ja,ko,zh-cn,ar,hi,tr,pl,nl,sv,da,no,fi,cs,hu,ro,bg,hr,sk,sl,et,lv,lt,am,om,ti,so,aa',
              // Remove the problematic layout option
              autoDisplay: false,
              multilanguagePage: true
            },
            googleTranslateRef.current
          );
          setIsLoaded(true);
          console.log('Google Translate initialized successfully');
        }
      } catch (err) {
        console.error('Error initializing Google Translate:', err);
        setError(true);
      }
    };

    // Check if Google Translate is already available
    if (window.google?.translate?.TranslateElement) {
      setTimeout(initializeGoogleTranslate, 100);
      return;
    }

    // Check if script is already loading
    const existingScript = document.querySelector(
      'script[src*="translate.google.com"]'
    );

    if (existingScript) {
      // Wait for existing script to load
      const checkInterval = setInterval(() => {
        if (window.google?.translate?.TranslateElement) {
          clearInterval(checkInterval);
          setTimeout(initializeGoogleTranslate, 100);
        }
      }, 500);

      // Timeout after 20 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!isLoaded) {
          console.warn(
            'Google Translate script timeout - falling back to manual translate'
          );
          setError(true);
        }
      }, 20000);
      return;
    }

    // Create and load the Google Translate script
    const loadGoogleTranslate = () => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src =
        'https://translate.google.com/translate_a/element.js?cb=initGoogleTranslate';
      script.async = true;
      script.onerror = () => {
        console.error('Failed to load Google Translate script');
        setError(true);
      };

      // Global callback function
      window.initGoogleTranslate = () => {
        setTimeout(initializeGoogleTranslate, 200);
      };

      document.head.appendChild(script);
    };

    loadGoogleTranslate();

    // Cleanup
    return () => {
      if (window.initGoogleTranslate) {
        delete window.initGoogleTranslate;
      }
    };
  }, [isLoaded, error]);

  // Fallback translation component
  const ManualTranslate = () => {
    const languages = [
      { code: '', name: 'Select Language' },
      { code: 'es', name: 'Español' },
      { code: 'fr', name: 'Français' },
      { code: 'de', name: 'Deutsch' },
      { code: 'it', name: 'Italiano' },
      { code: 'pt', name: 'Português' },
      { code: 'ru', name: 'Русский' },
      { code: 'ja', name: '日本語' },
      { code: 'ko', name: '한국어' },
      { code: 'zh-cn', name: '中文' },
      { code: 'ar', name: 'العربية' },
      { code: 'hi', name: 'हिन्दी' }
    ];

    const handleTranslate = (event) => {
      const lang = event.target.value;
      if (lang) {
        const url = `https://translate.google.com/translate?sl=auto&tl=${lang}&u=${encodeURIComponent(
          window.location.href
        )}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <select
        onChange={handleTranslate}
        className="bg-white border border-gray-300 rounded px-3 py-1 text-sm min-w-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500"
        defaultValue=""
        aria-label="Translate page"
      >
        {languages.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.name}
          </option>
        ))}
      </select>
    );
  };

  if (error) {
    return (
      <div className={`translate-fallback ${className}`}>
        <ManualTranslate />
      </div>
    );
  }

  return (
    <div className={`google-translate-container ${className}`}>
      <div ref={googleTranslateRef} />
      {!isLoaded && (
        <div className="translate-loading">
          <ManualTranslate />
        </div>
      )}
    </div>
  );
};

export default GoogleTranslate;
